/**
 * Shared sticky page header for all Catchball cycle-level pages:
 * Request Composer, Inbox, and Loops dashboard.
 *
 * Provides the same two-row header pattern the retired LoopWorkspacePage used, so all
 * four Catchball surfaces share a consistent chrome.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { CatchballLoopSubnav, type CatchballLoopSubnavActive } from './CatchballLoopSubnav';

export function CatchballStickyHeader({
  cycleId,
  active,
  workbenchLoopId,
}: {
  cycleId: string;
  active: CatchballLoopSubnavActive;
  /** Pass the current loop id when on the workbench so the tab link targets the right loop. */
  workbenchLoopId?: string | null;
}) {
  const cycles = useDegapProcessStore((s) => s.cycles);
  const cycle = useMemo(() => cycles.find((c) => c.id === cycleId), [cycles, cycleId]);

  return (
    <div className="sticky top-0 z-30 bg-[var(--bg-raised)] border-b border-[var(--rule)] dark:bg-[var(--bg-raised)] dark:border-[var(--rule)]">
      {/* Row 1: Cycle breadcrumb */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)]">
        <Link
          to={`/prioritization/cycles/${cycleId}`}
          className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {cycle?.name ?? 'Cycle'}
        </Link>
      </div>

      {/* Row 2: Tab navigation */}
      <div className="px-6 py-2">
        <CatchballLoopSubnav cycleId={cycleId} active={active} workbenchLoopId={workbenchLoopId} compact />
      </div>
    </div>
  );
}
